/**
 * Edge-table helpers for many-to-many relations.
 *
 * A relation is stored once and indexed in both directions, so neither side owns the other and
 * neither side has to be rewritten when the other changes. Per-edge attributes (the properties
 * that belong to the pairing rather than to either entity) live in a flat `attrs` map.
 *
 * Every operation is pure and returns the same table reference when nothing changed, so selectors
 * built on these tables keep their memoized results across unrelated updates.
 */

export const emptyRelationTable = Object.freeze({byLeft: {}, byRight: {}, attrs: {}});

/**
 * Builds the composite key used to store attributes for a single edge.
 * @param {String | Number} leftId Left entity id.
 * @param {String | Number} rightId Right entity id.
 * @returns {String} Attribute key for the edge.
 */
export const edgeKey = (leftId, rightId) => `${leftId}:${rightId}`;

/**
 * Appends an id to a list only when it is not already present.
 * @param {Array} list Existing id list, may be undefined.
 * @param {String | Number} id Id to append.
 * @returns {Array} The original list when the id was present, otherwise a new list.
 */
function withId(list, id) {
	if (!list) return [id];
	return list.includes(id) ? list : [...list, id];
}

/**
 * Removes an id from a list.
 * @param {Array} list Existing id list, may be undefined.
 * @param {String | Number} id Id to remove.
 * @returns {Array | undefined} A new list, or undefined when the list becomes empty.
 */
function withoutId(list, id) {
	if (!list?.length) return undefined;
	const next = list.filter((existing) => existing !== id);
	return next.length ? next : undefined;
}

/**
 * Assigns or deletes a key on an index depending on whether it still holds ids.
 * @param {Object} index Direction index (byLeft or byRight).
 * @param {String | Number} key Entity id acting as the key.
 * @param {Array | undefined} ids Remaining ids for that key.
 * @returns {Object} A new index.
 */
function setIndexEntry(index, key, ids) {
	const next = {...index};
	if (ids) {
		next[key] = ids;
	} else {
		delete next[key];
	}
	return next;
}

/**
 * Adds a single edge, merging attributes when the edge already exists.
 * @param {Object} table Relation table.
 * @param {String | Number} leftId Left entity id.
 * @param {String | Number} rightId Right entity id.
 * @param {Object} [attrs] Attributes belonging to this pairing.
 * @returns {Object} Updated relation table.
 */
export function addEdge(table, leftId, rightId, attrs) {
	return upsertEdges(table, [{leftId, rightId, attrs}]);
}

/**
 * Adds or updates many edges at once. Existing edges keep their identity and merge attributes,
 * which is what makes a paged list response additive rather than destructive.
 * @param {Object} table Relation table.
 * @param {Array<{leftId: (String | Number), rightId: (String | Number), attrs: Object}>} edges Edges to upsert.
 * @returns {Object} Updated relation table.
 */
export function upsertEdges(table, edges) {
	if (!edges?.length) return table;

	let byLeft = table.byLeft;
	let byRight = table.byRight;
	let attrs = table.attrs;
	let changed = false;

	for (const edge of edges) {
		if (!edge) continue;
		const {leftId, rightId} = edge;
		if (leftId === null || leftId === undefined || rightId === null || rightId === undefined) continue;

		const nextRightIds = withId(byLeft[leftId], rightId);
		if (nextRightIds !== byLeft[leftId]) {
			byLeft = byLeft === table.byLeft ? {...byLeft} : byLeft;
			byLeft[leftId] = nextRightIds;
			changed = true;
		}

		const nextLeftIds = withId(byRight[rightId], leftId);
		if (nextLeftIds !== byRight[rightId]) {
			byRight = byRight === table.byRight ? {...byRight} : byRight;
			byRight[rightId] = nextLeftIds;
			changed = true;
		}

		if (edge.attrs) {
			const key = edgeKey(leftId, rightId);
			attrs = attrs === table.attrs ? {...attrs} : attrs;
			attrs[key] = {...attrs[key], ...edge.attrs};
			changed = true;
		}
	}

	if (!changed) return table;

	return {
		byLeft: byLeft === table.byLeft ? {...byLeft} : byLeft,
		byRight,
		attrs
	};
}

/**
 * Removes a single edge and its attributes.
 * @param {Object} table Relation table.
 * @param {String | Number} leftId Left entity id.
 * @param {String | Number} rightId Right entity id.
 * @returns {Object} Updated relation table.
 */
export function removeEdge(table, leftId, rightId) {
	if (!table.byLeft[leftId]?.includes(rightId)) return table;

	const attrs = {...table.attrs};
	delete attrs[edgeKey(leftId, rightId)];

	return {
		byLeft: setIndexEntry(table.byLeft, leftId, withoutId(table.byLeft[leftId], rightId)),
		byRight: setIndexEntry(table.byRight, rightId, withoutId(table.byRight[rightId], leftId)),
		attrs
	};
}

/**
 * Removes every edge attached to a left entity.
 * @param {Object} table Relation table.
 * @param {String | Number} leftId Left entity id.
 * @returns {Object} Updated relation table.
 */
export function removeAllForLeft(table, leftId) {
	const rightIds = table.byLeft[leftId];
	if (!rightIds?.length) return table;

	const byLeft = {...table.byLeft};
	delete byLeft[leftId];

	let byRight = {...table.byRight};
	const attrs = {...table.attrs};

	for (const rightId of rightIds) {
		byRight = setIndexEntry(byRight, rightId, withoutId(byRight[rightId], leftId));
		delete attrs[edgeKey(leftId, rightId)];
	}

	return {byLeft, byRight, attrs};
}

/**
 * Removes every edge attached to a right entity.
 * @param {Object} table Relation table.
 * @param {String | Number} rightId Right entity id.
 * @returns {Object} Updated relation table.
 */
export function removeAllForRight(table, rightId) {
	const leftIds = table.byRight[rightId];
	if (!leftIds?.length) return table;

	const byRight = {...table.byRight};
	delete byRight[rightId];

	let byLeft = {...table.byLeft};
	const attrs = {...table.attrs};

	for (const leftId of leftIds) {
		byLeft = setIndexEntry(byLeft, leftId, withoutId(byLeft[leftId], rightId));
		delete attrs[edgeKey(leftId, rightId)];
	}

	return {byLeft, byRight, attrs};
}

/**
 * Replaces the complete edge set of a left entity. Used when a detail response is the authority on
 * what a single account is related to, so edges dropped by the server disappear locally too.
 * @param {Object} table Relation table.
 * @param {String | Number} leftId Left entity id.
 * @param {Array<{rightId: (String | Number), attrs: Object}>} edges Replacement edges for that left entity.
 * @returns {Object} Updated relation table.
 */
export function replaceEdgesForLeft(table, leftId, edges) {
	const cleared = removeAllForLeft(table, leftId);
	if (!edges?.length) return cleared;

	return upsertEdges(
		cleared,
		edges.map((edge) => ({leftId, rightId: edge.rightId, attrs: edge.attrs}))
	);
}

/**
 * Reads the right ids related to a left entity.
 * @param {Object} table Relation table.
 * @param {String | Number} leftId Left entity id.
 * @returns {Array} Related right ids, empty when none.
 */
export function getRightIds(table, leftId) {
	return table?.byLeft?.[leftId] ?? [];
}

/**
 * Reads the left ids related to a right entity.
 * @param {Object} table Relation table.
 * @param {String | Number} rightId Right entity id.
 * @returns {Array} Related left ids, empty when none.
 */
export function getLeftIds(table, rightId) {
	return table?.byRight?.[rightId] ?? [];
}

/**
 * Reads the attributes stored on a single edge.
 * @param {Object} table Relation table.
 * @param {String | Number} leftId Left entity id.
 * @param {String | Number} rightId Right entity id.
 * @returns {Object | undefined} Edge attributes when present.
 */
export function getEdgeAttrs(table, leftId, rightId) {
	return table?.attrs?.[edgeKey(leftId, rightId)];
}
